// TODO(API--Unsubscribe_From_Marketing_Emails): Resolve per recipient at send time.
export const EMAIL_BUILDER_UNSUBSCRIBE_MERGE_TAG_KEY = 'customer.unsubscribe_url';

export const EMAIL_BUILDER_PREFERENCES_MERGE_TAG_KEY = 'customer.preferences_url';

export type MergeTagKey =
  | 'customer.full_name'
  | 'customer.first_name'
  | 'customer.last_name'
  | 'customer.email'
  | typeof EMAIL_BUILDER_UNSUBSCRIBE_MERGE_TAG_KEY
  | typeof EMAIL_BUILDER_PREFERENCES_MERGE_TAG_KEY
  | 'facility.long_name'
  | 'facility.full_name'
  | 'facility.phone'
  | 'facility.email'
  | 'facility.full_address'
  | 'facility.address_line_1'
  | 'facility.address_line_2'
  | 'facility.city'
  | 'facility.province_code'
  | 'facility.province_name'
  | 'facility.postal'
  | 'facility.country_name'
  | 'client.website_url'
  | 'system.current_year';

export type MergeTag = {
  key: MergeTagKey;
  label: string;
  sampleValue: string;
};

export type MergeTagCategory = {
  category: string;
  tags: MergeTag[];
};

export const MERGE_TAG_CATALOG: MergeTagCategory[] = [
  {
    category: 'Customer',
    tags: [
      {
        key: 'customer.full_name',
        label: 'Customer full name',
        sampleValue: 'Alex Morgan',
      },
      {
        key: 'customer.first_name',
        label: 'Customer first name',
        sampleValue: 'Alex',
      },
      {
        key: 'customer.last_name',
        label: 'Customer last name',
        sampleValue: 'Morgan',
      },
      {
        key: 'customer.email',
        label: 'Customer email',
        sampleValue: 'alex@example.com',
      },
      {
        key: EMAIL_BUILDER_UNSUBSCRIBE_MERGE_TAG_KEY,
        label: 'Unsubscribe URL',
        sampleValue: '#',
      },
      {
        key: EMAIL_BUILDER_PREFERENCES_MERGE_TAG_KEY,
        label: 'Email preferences URL',
        sampleValue: 'https://admin.teeon.com/tee-on/portal/preferences',
      },
    ],
  },
  {
    category: 'Facility',
    tags: [
      {
        key: 'facility.long_name',
        label: 'Facility name',
        sampleValue: 'Sample Golf Club',
      },
      {
        key: 'facility.full_name',
        label: 'Facility full name',
        sampleValue: 'Sample Golf Club',
      },
      {
        key: 'facility.phone',
        label: 'Phone',
        sampleValue: '(706) 555-0142',
      },
      {
        key: 'facility.email',
        label: 'Email',
        sampleValue: 'hello@example.com',
      },
      {
        key: 'facility.full_address',
        label: 'Full address',
        sampleValue: '1200 Fairway Drive, Augusta, GA 30901',
      },
      {
        key: 'facility.address_line_1',
        label: 'Address line 1',
        sampleValue: '1200 Fairway Drive',
      },
      {
        key: 'facility.address_line_2',
        label: 'Address line 2',
        sampleValue: 'Suite 100',
      },
      { key: 'facility.city', label: 'City', sampleValue: 'Augusta' },
      {
        key: 'facility.province_code',
        label: 'Province code',
        sampleValue: 'GA',
      },
      {
        key: 'facility.province_name',
        label: 'Province name',
        sampleValue: 'Georgia',
      },
      { key: 'facility.postal', label: 'Postal code', sampleValue: '30901' },
      {
        key: 'facility.country_name',
        label: 'Country',
        sampleValue: 'United States',
      },
    ],
  },
  {
    category: 'Client',
    tags: [
      {
        key: 'client.website_url',
        label: 'Website URL',
        sampleValue: 'https://example.com',
      },
    ],
  },
  {
    category: 'System',
    tags: [
      {
        key: 'system.current_year',
        label: 'Current year',
        sampleValue: String(new Date().getFullYear()),
      },
    ],
  },
];

const mergeTagKeys: MergeTagKey[] = [];
const sampleByKey = new Map<string, string>();
const labelByKey = new Map<string, string>();

for (const group of MERGE_TAG_CATALOG) {
  for (const tag of group.tags) {
    mergeTagKeys.push(tag.key);
    sampleByKey.set(tag.key, tag.sampleValue);
    labelByKey.set(tag.key, tag.label);
  }
}

export const MERGE_TAG_KEYS: readonly MergeTagKey[] = mergeTagKeys;

const MERGE_TAG_LINK_URL_SCHEME_PREFIX: Partial<Record<MergeTagKey, string>> = {
  'facility.phone': 'tel:',
  'facility.email': 'mailto:',
  'customer.email': 'mailto:',
};

export function isMergeTagKey(value: string): value is MergeTagKey {
  return labelByKey.has(value);
}

export function mergeTagLabel(key: MergeTagKey): string {
  return labelByKey.get(key) ?? key;
}

export function wrapMergeTag(key: string): string {
  return `{{${key}}}`;
}

export function wrapMergeTagForLinkUrl(key: MergeTagKey): string {
  const prefix = MERGE_TAG_LINK_URL_SCHEME_PREFIX[key] ?? '';
  return `${prefix}${wrapMergeTag(key)}`;
}

export function formatMergeTag(key: MergeTagKey): string {
  return wrapMergeTag(key);
}

export function applyMergeTags(input: string, mode: 'raw' | 'sample'): string {
  if (mode === 'raw') {
    return input;
  }
  return input.replace(/\{\{([^}]+)\}\}/g, (match, key: string) => {
    return sampleByKey.get(key.trim()) ?? match;
  });
}
